import React, { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { setCopyMetadata } from '@disco/state-management/actions/copy-metadata-actions'
import { getLocationBeforeTransitions } from '@disco/state-management/selectors/routing'

import type { TrackId } from 'types/track'

import { getShortlistBaseRoute } from 'utils/routing'

import { LuEllipsisVertical } from '@react-icons/all-files/lu/LuEllipsisVertical'
import { DropdownMenu, IconButton } from 'components/ds'
import Button from 'components/ui/button'
import EllipsisMenu from 'components/ui/ellipsis-menu'

import { useDispatch, useSelector } from 'hooks/use-redux'

type Props = {
  trackIds: TrackId[]
  /**
   * - `default`: captioned "Copy track info" dropdown button
   * - `inline`: plain inline link
   * - `icon`: icon-only ellipsis menu, for modal headers
   */
  variant?: 'default' | 'inline' | 'icon'
}

const InlineCopyMetadataButton = ({ trackIds }: { trackIds: TrackId[] }) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useSelector(getLocationBeforeTransitions)

  const getCopyMetadataRoute = useCallback(() => {
    const baseRoute = getShortlistBaseRoute(location)
    return baseRoute ? `${baseRoute}/copy-metadata` : '/explorer/copy-metadata'
  }, [location])

  const setCopyMetadataTarget = useCallback(() => {
    dispatch(
      setCopyMetadata({
        targetTrackIds: trackIds,
      }),
    )
    navigate(getCopyMetadataRoute())
    dispatch(sendMixpanelEvent('Copy track info to track (modal)'))
  }, [dispatch, trackIds, getCopyMetadataRoute, navigate])

  return (
    <Button
      appearance="inline-link"
      caption={
        trackIds.length > 1
          ? 'Copy track info to these tracks'
          : 'Copy track info to this track'
      }
      onClick={setCopyMetadataTarget}
    />
  )
}

const DefaultCopyMetadataButton = ({
  trackIds,
  iconOnly,
}: {
  trackIds: TrackId[]
  iconOnly?: boolean
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useSelector(getLocationBeforeTransitions)

  const getCopyMetadataRoute = useCallback(() => {
    const baseRoute = getShortlistBaseRoute(location)
    return baseRoute ? `${baseRoute}/copy-metadata` : '/explorer/copy-metadata'
  }, [location])

  const setCopyMetadataSource = useCallback(() => {
    dispatch(
      setCopyMetadata({
        sourceTrackId: trackIds[0],
      }),
    )
    navigate(getCopyMetadataRoute())
    dispatch(sendMixpanelEvent('Copy track info from track (modal)'))
  }, [dispatch, trackIds, getCopyMetadataRoute, navigate])
  const setCopyMetadataTarget = useCallback(() => {
    dispatch(
      setCopyMetadata({
        targetTrackIds: trackIds,
      }),
    )
    navigate(getCopyMetadataRoute())
    dispatch(sendMixpanelEvent('Copy track info to track (modal)'))
  }, [dispatch, trackIds, getCopyMetadataRoute, navigate])
  const previewMetadata = useCallback(() => {
    if (trackIds[0]) {
      navigate(`/explorer/tracks/${trackIds[0]}/preview-metadata`)
      dispatch(sendMixpanelEvent('Preview track info (modal)'))
    }
  }, [dispatch, trackIds, navigate])
  const options = useMemo(
    () => [
      {
        label: 'Copy track info to this track',
        onClick: setCopyMetadataTarget,
      },
      {
        label: 'Copy track info from this track',
        onClick: setCopyMetadataSource,
      },
      {
        label: 'Preview track info',
        onClick: previewMetadata,
      },
    ],
    [setCopyMetadataTarget, setCopyMetadataSource, previewMetadata],
  )

  if (iconOnly) {
    const items =
      trackIds.length > 1
        ? [
            {
              label: 'Copy track info to these tracks',
              onClick: setCopyMetadataTarget,
            },
          ]
        : options

    return (
      <DropdownMenu.Root>
        <DropdownMenu.Trigger>
          <IconButton
            size="2"
            variant="ghost"
            color="gray"
            aria-label="More actions"
          >
            <LuEllipsisVertical size={20} />
          </IconButton>
        </DropdownMenu.Trigger>
        <DropdownMenu.Content>
          {items.map(item => (
            <DropdownMenu.Item key={item.label} onSelect={item.onClick}>
              {item.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    )
  }

  return trackIds.length > 1 ? (
    <Button
      caption="Copy track info to these tracks"
      onClick={setCopyMetadataTarget}
    />
  ) : (
    <EllipsisMenu compact caption="Copy track info" options={options} />
  )
}

const CopyMetadataButton = ({ trackIds, variant = 'default' }: Props) => {
  if (variant === 'inline') {
    return <InlineCopyMetadataButton trackIds={trackIds} />
  }
  return (
    <DefaultCopyMetadataButton
      trackIds={trackIds}
      iconOnly={variant === 'icon'}
    />
  )
}

export default React.memo<Props>(CopyMetadataButton)
